import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DownloadIcon from "@mui/icons-material/Download";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import { PDFDocument } from "pdf-lib";
import { saveAs } from "file-saver";

const ReportImageModal = ({ open, onClose, files }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);

  /* ✅ DOWNLOAD FUNCTION (VIDEO ONLY) */
  const downloadFile = async (url) => {
    try {
      setDownloading(true);
      setProgress(0);

      const response = await fetch(url);
      const contentLength = response.headers.get("content-length");

      if (!response.body) {
        throw new Error("ReadableStream not supported");
      }

      const reader = response.body.getReader();
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      let received = 0;
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        received += value.length;

        if (total) {
          setProgress(Math.round((received / total) * 100));
        }
      }

      const blob = new Blob(chunks);
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop();
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      // ✅ CLOSE MODAL AFTER SUCCESS
      setConfirmOpen(false);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloading(false);
      setProgress(0);
    }
  };

  const handleDownload = async (url, type) => {
    try {
      if (type === "pdf") {
        const response = await fetch(url);
        const blob = await response.blob();
        saveAs(blob, url.split('/').pop());
      } else if (type === "image") {
        const response = await fetch(url);
        const imageBlob = await response.blob();
        const imageBytes = await imageBlob.arrayBuffer();

        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage();

        let image;
        if (url.toLowerCase().endsWith('.png')) {
          image = await pdfDoc.embedPng(imageBytes);
        } else {
          image = await pdfDoc.embedJpg(imageBytes);
        }

        const { width, height } = image.scale(0.5); // scale to fit
        page.drawImage(image, {
          x: (page.getWidth() - width) / 2,
          y: (page.getHeight() - height) / 2,
          width,
          height,
        });

        const pdfBytes = await pdfDoc.save();
        const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
        const filename = url.split('/').pop().replace(/\.[^/.]+$/, ".pdf");
        saveAs(pdfBlob, filename);
      }
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleVideoClick = (url) => {
    setDownloadUrl(url);
    setConfirmOpen(true);
  };

  const getFileType = (url) => {
    if (url.endsWith(".pdf")) return "pdf";
    if (url.endsWith(".mp4")) return "video";
    return "image";
  };

  return (
    <>
      {/* MAIN FILE LIST MODAL */}
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", color: "#0FB7A4" }}
        >
          Report Files
          <IconButton onClick={onClose} sx={{ marginLeft: "auto" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <List disablePadding>
            {files.map((file, index) => {
              const fileUrl = file.ImageFile;
              const type = getFileType(fileUrl);

              return (
                <ListItemButton
                  key={index}
                  sx={{
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    gap: 2,
                  }}
                  onClick={() => {
                    if (type === "video") {
                      handleVideoClick(fileUrl);
                    } else {
                      window.open(fileUrl, "_blank");
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {/* ICON */}
                    {type === "pdf" && <PictureAsPdfIcon color="error" />}
                    {type === "image" && <ImageIcon color="primary" />}
                    {type === "video" && <VideoFileIcon color="success" />}

                    <ListItemText
                      primary={`File ${index + 1}`}
                      secondary={
                        type === "pdf"
                          ? "PDF File"
                          : type === "video"
                          ? "Video File (Download)"
                          : "Image File"
                      }
                    />

                    {type !== "video" && (
                      <IconButton 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleDownload(fileUrl, type); 
                        }}
                        sx={{ ml: 'auto' }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      {/* CONFIRM DOWNLOAD POPUP (VIDEO ONLY) */}
      <Dialog
        open={confirmOpen}
        onClose={!downloading ? () => setConfirmOpen(false) : null}
      >
        <DialogTitle>Download Video</DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2 }}>
            This video will be downloaded to your device.
          </Box>

          {downloading && (
            <>
              <LinearProgress
                variant={progress ? "determinate" : "indeterminate"}
                value={progress}
                sx={{ mb: 1 }}
              />
              {progress > 0 && (
                <Box sx={{ fontSize: 12, textAlign: "right" }}>{progress}%</Box>
              )}
            </>
          )}

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <Button
              disabled={downloading}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disabled={downloading}
              sx={{
                backgroundColor: "#0FB7A4",
                "&:hover": { backgroundColor: "#0FB7A4" },
              }}
              onClick={() => downloadFile(downloadUrl)}
              startIcon={
                downloading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              {downloading ? "Downloading..." : "Download"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportImageModal;
